import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated trust counters. Numbers count up when the strip scrolls into view.
 * Supports integers and one-decimal values (e.g. 4.9).
 */
function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  const isFloat = !Number.isInteger(target);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return isFloat ? val.toFixed(1) : Math.round(val);
}

function StatItem({ stat, active }) {
  const display = useCountUp(Number(stat.value) || 0, active);
  return (
    <div className="text-center" data-testid="trust-stat-item">
      <div className="font-display text-4xl md:text-5xl font-extrabold"
        style={{
          background: 'linear-gradient(135deg, #059669 0%, #023E8A 50%, #7C3AED 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>
        {display}<span>{stat.suffix}</span>
      </div>
      <div className="text-slate-600 text-sm mt-2 font-medium">{stat.label}</div>
    </div>
  );
}

export default function TrustStats({ title, stats = [] }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [stats.length]);

  if (!stats.length) return null;

  return (
    <section ref={ref} className="py-14 md:py-16 border-y border-slate-100 bg-white" data-testid="trust-stats">
      <div className="container-x">
        {title && (
          <h2 className="sr-only">{title}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => <StatItem key={i} stat={s} active={active} />)}
        </div>
      </div>
    </section>
  );
}
